/**
 * 가정통신문 양식/예시 - SEO 롱테일 페이지
 * 
 * 타겟 키워드: 가정통신문 양식, 가정통신문 예시, 가정통신문 샘플, 가정통신문 작성
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import { useToast } from '../../components/Toast';
import { DICTIONARY_LINKS } from '../../constants/seoDictionaryLinks';
import '../../styles/design-system/index.css';

const TongsinmunGuidePage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState('새학기');

  const categories = ['새학기', '행사안내', '안전교육', '수행평가', '방학'];

  const templates = {
    '새학기': [
      {
        title: '새학기 학부모 안내문',
        preview: `안녕하십니까? 학부모님의 가정에 건강과 행복이 함께 하시길 바랍니다.

2026학년도 새 학기가 시작되었습니다. 올해도 학생들의 성장과 발전을 위해 최선을 다하겠습니다.

아래 사항을 안내해 드리오니 참고하시기 바랍니다.

1. 개학일: 20XX년 X월 X일 (X)
2. 등교 시간: 08:30까지
3. 준비물: 실내화, 필기도구
4. 담임 상담: 개별 안내 예정

가정에서도 관심과 협조 부탁드립니다.

20XX년 X월 X일
OO학교장`
      }
    ],
    '행사안내': [
      {
        title: '현장체험학습 안내문',
        preview: `안녕하십니까? 학부모님의 가정에 평안을 기원합니다.

우리 학교에서는 학생들의 다양한 체험 기회를 위해 현장체험학습을 계획하고 있습니다.

• 일시: 20XX년 X월 X일 (X) 09:00~16:00
• 장소: OOOO
• 대상: X학년 전체
• 비용: OO,OOO원 (버스비, 입장료 포함)
• 준비물: 편한 복장, 도시락, 물

아래 회신서를 X월 X일까지 제출해 주시기 바랍니다.

OO학교장`
      }
    ],
    '안전교육': [
      {
        title: '교통안전 교육 안내문',
        preview: `학부모님 안녕하십니까?

우리 학교에서는 학생 안전을 위해 교통안전 교육을 실시합니다.

1. 일시: 20XX년 X월 X일 (X) O교시
2. 내용: 보행 안전, 자전거 안전, 대중교통 이용법
3. 강사: OO경찰서 교통안전계

가정에서도 등·하교 시 안전에 대해 대화 나눠주시면 감사하겠습니다.

OO학교장`
      }
    ],
    '수행평가': [
      {
        title: '수행평가 안내문',
        preview: `학부모님 안녕하십니까?

X학기 수행평가 일정을 안내해 드립니다.

[X학년 수행평가 일정]
• 국어: X월 X일 / 논술형 평가
• 수학: X월 X일 / 문제해결 과정 평가
• 영어: X월 X일 / 말하기 평가

학생들이 평소 실력을 발휘할 수 있도록 가정에서도 격려 부탁드립니다.

OO학교장`
      }
    ],
    '방학': [
      {
        title: '여름방학 안내문',
        preview: `학부모님 안녕하십니까?

X학기를 마무리하고 여름방학에 들어갑니다.

1. 방학 기간: 20XX.X.X(X) ~ 20XX.X.X(X)
2. 개학일: 20XX년 X월 X일 (X)
3. 방학 과제: 별도 안내
4. 방학 중 연락처: XXX-XXXX-XXXX

건강하고 알찬 방학 되시길 바랍니다.

OO학교장`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>가정통신문 양식 & 예시 모음 | 새학기·행사·안전교육·수행평가·방학</title>
        <meta name="description" content="가정통신문 양식 및 예시 모음. 새학기, 현장체험학습, 안전교육, 수행평가, 방학 안내문. 복사해서 바로 쓰는 템플릿. AI로 우리 학교에 맞게 자동 수정." />
        <meta name="keywords" content="가정통신문 양식, 가정통신문 예시, 가정통신문 샘플, 가정통신문 작성, 가정통신문 템플릿, 학부모 안내문, 현장체험학습 안내문, 수행평가 안내문, 방학 안내문" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/tongsinmun" />
        
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/tongsinmun" />
        <meta property="og:title" content="가정통신문 양식 & 예시 모음 | 상황별 템플릿" />
        <meta property="og:description" content="새학기, 행사, 안전교육, 수행평가, 방학 가정통신문 예시. 복사해서 바로 사용." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png?v=20260210" />
        <meta property="og:locale" content="ko_KR" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "가정통신문 작성하는 방법",
            "description": "상황별 가정통신문 작성 가이드",
            "step": [
              { "@type": "HowToStep", "name": "상황 선택", "text": "새학기, 행사, 안전교육 등 상황 선택" },
              { "@type": "HowToStep", "name": "템플릿 선택", "text": "상황에 맞는 가정통신문 템플릿 선택" },
              { "@type": "HowToStep", "name": "내용 수정", "text": "학교명, 날짜, 세부 내용 수정" },
              { "@type": "HowToStep", "name": "회신서 첨부", "text": "필요시 하단에 회신서 양식 추가" }
            ]
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'var(--whatquiz-font-family)',
        backgroundColor: '#fff',
        minHeight: '100vh'
      }}>
        {/* 히어로 */}
        <section style={{
          padding: '80px 20px 60px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #eff6ff 0%, #fff 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#dbeafe',
              color: '#1d4ed8',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              복사해서 바로 사용
            </span>
            
            <h1 style={{
              fontSize: 'clamp(26px, 5vw, 40px)',
              fontWeight: '800',
              color: '#111',
              lineHeight: '1.3',
              marginBottom: '20px'
            }}>
              가정통신문<br />
              <span style={{ color: '#3182f6' }}>양식 & 예시</span>
            </h1>
            
            <p style={{
              fontSize: '17px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              새학기, 행사, 안전교육, 수행평가, 방학<br />
              <strong>상황별 템플릿 모음</strong>
            </p>

            <button
              onClick={() => navigate('/chat')}
              style={{
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: '#3182f6',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              AI로 우리 학교에 맞게 작성하기 →
            </button>
          </div>
        </section>

        {/* 카테고리 선택 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            상황 선택
          </h2>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            justifyContent: 'center',
            marginBottom: '40px'
          }}>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: selectedCategory === cat ? '#fff' : '#374151',
                  backgroundColor: selectedCategory === cat ? '#3182f6' : '#f1f5f9',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* 템플릿 표시 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {templates[selectedCategory]?.map((template, idx) => (
              <div key={idx} style={{
                padding: '24px',
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111'
                  }}>
                    📄 {template.title}
                  </h3>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(template.preview);
                      showToast('복사했어요', 'success');
                    }}
                    style={{
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1d4ed8',
                      backgroundColor: '#dbeafe',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    복사하기
                  </button>
                </div>
                
                <pre style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  color: '#374151',
                  lineHeight: '1.8',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  margin: 0,
                  overflow: 'auto'
                }}>
                  {template.preview}
                </pre>
              </div>
            ))}
          </div>
        </section>

        {/* 작성 팁 */}
        <section style={{
          padding: '60px 20px',
          backgroundColor: '#f8fafc'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              좋은 가정통신문 작성 팁
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '16px'
            }}>
              {[
                { icon: '📝', tip: '핵심 내용은 번호로 정리', desc: '날짜, 장소, 준비물 등을 명확하게' },
                { icon: '📅', tip: '회신 마감일 명시', desc: '며칠까지 제출인지 명확히' },
                { icon: '📞', tip: '문의처 안내', desc: '담당자 연락처 하단 기재' },
                { icon: '✂️', tip: '회신서 별도 구분', desc: '점선으로 구분하여 자르기 쉽게' }
              ].map((item, idx) => (
                <div key={idx} style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '28px' }}>{item.icon}</span>
                  <h4 style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111',
                    margin: '12px 0 8px'
                  }}>{item.tip}</h4>
                  <p style={{
                    fontSize: '13px',
                    color: '#64748b',
                    margin: 0
                  }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px'
            }}>
              우리 학교에 맞게<br />
              <span style={{ color: '#3182f6' }}>AI가 자동 작성</span>
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              학교명, 행사명, 날짜만 입력하면<br />
              <strong>1분 만에 가정통신문 완성</strong>
            </p>

            <button
              onClick={() => navigate('/chat')}
              style={{
                padding: '16px 40px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: '#3182f6',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              AI로 가정통신문 작성하기
            </button>
          </div>
        </section>

        {/* 관련 가이드 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111',
            marginBottom: '16px'
          }}>
            관련 가이드
          </h3>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <Link to="/guide/saenggibu-2026" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              📋 2026 생기부 기재요령
            </Link>
            <Link to="/guide/setak-example" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              ✍️ 세특 예시 모음
            </Link>
            <Link to="/create-saenggibu" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              🤖 AI 생기부 작성
            </Link>
            {[DICTIONARY_LINKS.schoolDocs, DICTIONARY_LINKS.winterVacation, DICTIONARY_LINKS.assessmentNotice].map((link) => (
              <a key={link.href} href={link.href} style={{
                padding: '12px 20px',
                backgroundColor: '#f1f5f9',
                borderRadius: '8px',
                color: '#334155',
                textDecoration: 'none',
                fontSize: '14px'
              }}>
                {link.label}
              </a>
            ))}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TongsinmunGuidePage;
